import {
  applyCooking,
  type CookingMethod,
  type FastfoodItem,
  looksCookable,
  type MealSlot,
  scaleToGrams,
} from '@gramas/shared'
import {
  Button,
  EmptyState,
  IconButton,
  List,
  ListRow,
  MacroPreview,
  MicIcon,
  NumberField,
  SearchInput,
  SectionLabel,
  Segmented,
  Sheet,
  Switch,
} from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useMemo, useState } from 'react'
import {
  type FastfoodChainSummary,
  fastfoodChains,
  searchFastfoodItems,
  searchFoodsEverywhere,
} from '../../api/foods.js'
import { db, type LocalFood, type LocalRecipe } from '../../db/dexie.js'
import {
  favouriteItems,
  foodDisplayName,
  foodPer100g,
  isFavourite,
  logAdHoc,
  logCookingOil,
  logFood,
  logRecipe,
  mostLogged,
  type QuickAddItem,
  recentlyLogged,
  slotFor,
  toggleFavourite,
} from '../../db/repo.js'
import { PersonaSpinner } from '../../glitime/PersonaSpinner.js'
import { logContext } from '../../sass/context.js'
import { sassLine } from '../../sass/engine.js'
import { maybeLogToast } from '../../sass/toast.js'
import { useSassOnce } from '../../sass/useSassOnce.js'
import { syncNow } from '../../sync/engine.js'
import { asOrdered, cupsForItem } from '../FastFood.js'
import { COOKING_LABEL, CookingPicker } from './CookingPicker.js'
import { placeCounterItems } from './counter-rank.js'
import { DishEstimateSheet } from './DishEstimateSheet.js'
import { QuantityPresets } from './QuantityPresets.js'
import { SlotPicker } from './SlotPicker.js'

type QuickTab = 'recent' | 'often' | 'starred'

/** A food the sheet should open already holding, skipping the search step. */
export interface QuickAddPick {
  kind: 'food' | 'recipe'
  id: string
  grams: number
}

export interface QuickAddSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  slot: MealSlot
  date: string
  /**
   * Arrive on the quantity step with this already chosen.
   *
   * Used by the suggestions on the home screen: having said "180 g of this
   * fits", opening a search box would be asking the question over again. The
   * amount is still a field, so the suggestion is a starting point rather than
   * a decision made for someone.
   */
  preselect?: QuickAddPick | null
  /**
   * Hand the job to voice instead. The parent owns that sheet rather than this
   * one opening it, because a dialog inside a dialog traps focus in the wrong
   * layer and leaves the phone with two stacked drag handles.
   */
  onVoice?: () => void
}

/**
 * Search, then quantity, in one sheet. Local results appear as the user types
 * and the server's are merged in when they arrive, so the common case — a food
 * eaten before — never waits on the network.
 */
export function QuickAddSheet({
  open,
  onOpenChange,
  slot: initialSlot,
  date,
  preselect = null,
  onVoice,
}: QuickAddSheetProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<LocalFood[]>([])
  const [counter, setCounter] = useState<FastfoodItem[]>([])
  const [chains, setChains] = useState<FastfoodChainSummary[]>([])
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState<LocalFood | null>(null)
  const [selectedRecipe, setSelectedRecipe] = useState<LocalRecipe | null>(null)
  const [slot, setSlot] = useState<MealSlot>(initialSlot)
  const [grams, setGrams] = useState<number | null>(100)
  const [tab, setTab] = useState<QuickTab>('recent')
  const [method, setMethod] = useState<CookingMethod>('raw')
  const [estimating, setEstimating] = useState(false)

  // Everything anyone eats twice ends up in one of these three lists, which is
  // why the sheet opens on them rather than on an empty search box.
  const quick = useLiveQuery(
    () => (tab === 'recent' ? recentlyLogged() : tab === 'often' ? mostLogged() : favouriteItems()),
    [tab, open],
    [],
  )

  useEffect(() => {
    if (!open || chains.length > 0) return
    void fastfoodChains()
      .then(setChains)
      .catch(() => {})
  }, [open, chains.length])

  useEffect(() => {
    if (open) {
      setSlot(initialSlot)
      setQuery('')
      setResults([])
      setCounter([])
      setSelected(null)
      setSelectedRecipe(null)
      setGrams(100)
      setMethod('raw')
      setEstimating(false)
    }
  }, [open, initialSlot])

  // After the reset above, which runs first because it is declared first.
  useEffect(() => {
    if (!open || preselect == null) return
    let cancelled = false
    void (async () => {
      const chosen =
        preselect.kind === 'recipe'
          ? await db.recipes.get(preselect.id)
          : await db.foods.get(preselect.id)
      if (cancelled || chosen == null) return
      if (preselect.kind === 'recipe') setSelectedRecipe(chosen as LocalRecipe)
      else setSelected(chosen as LocalFood)
      setGrams(preselect.grams)
      setMethod('raw')
    })()
    return () => {
      cancelled = true
    }
  }, [open, preselect])

  const pickQuick = async (item: QuickAddItem) => {
    if (item.kind === 'recipe') {
      const recipe = await db.recipes.get(item.id)
      if (!recipe) return
      setSelectedRecipe(recipe)
      setGrams(100)
      setMethod('raw')
      return
    }
    const food = await db.foods.get(item.id)
    if (!food) return
    setSelected(food)
    setGrams(food.servingSizeG ?? 100)
    setMethod('raw')
  }

  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setResults([])
      setCounter([])
      return
    }
    let cancelled = false
    setSearching(true)
    const handle = window.setTimeout(async () => {
      const [merged, chainItems] = await Promise.all([
        searchFoodsEverywhere(query, 25, (local) => {
          if (!cancelled) setResults(local)
        }),
        searchFastfoodItems(query).catch(() => []),
      ])
      if (!cancelled) {
        setResults(merged)
        setCounter(chainItems)
        setSearching(false)
      }
    }, 180)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [query, open])

  // Which of the two lists actually answered better, decided on one scale
  // rather than by which array the rows arrived in.
  const placed = useMemo(
    () => placeCounterItems(query, counter, results),
    [query, counter, results],
  )

  const chosen = selected ?? selectedRecipe
  // A recipe is always weighed; only a catalogue product can be a drink.
  const unit = selected?.unit ?? 'g'
  const chosenName = selected ? foodDisplayName(selected) : (selectedRecipe?.name ?? '')
  const [starred, setStarred] = useState(false)

  useEffect(() => {
    if (!chosen) return setStarred(false)
    void isFavourite(selected ? 'food' : 'recipe', chosen.id).then(setStarred)
  }, [chosen, selected])

  /**
   * Weighing raw is the accurate measurement, not an approximation.
   *
   * Cooking removes water, not energy, so the macros of what was weighed are
   * the macros of what gets eaten — apart from whatever oil went in the pan,
   * which is the one thing worth asking about.
   */
  const cookable = selected != null && looksCookable(foodDisplayName(selected))
  const cooked =
    chosen && grams ? applyCooking(foodPer100g(chosen), grams, unit === 'g' ? method : 'raw') : null
  const preview = useMemo(
    () => (chosen && grams ? scaleToGrams(foodPer100g(chosen), grams) : null),
    [chosen, grams],
  )

  const quickEmpty = (quick ?? []).length === 0
  const quickSurface = tab === 'starred' ? 'favourites' : 'quickadd'
  const quickEmptyTitle = useSassOnce(quickEmpty ? quickSurface : null, () => {
    const fallback = tab === 'starred' ? 'Mėgstamų dar nėra' : 'Dar nieko neįrašyta'
    return sassLine('empty_list', { surface: quickSurface }) ?? fallback
  })

  const searchEmpty =
    !searching && query.trim().length >= 2 && results.length === 0 && placed.items.length === 0
  const searchEmptyTitle = useSassOnce(
    searchEmpty ? 'search' : null,
    () => sassLine('empty_list', { surface: 'search' }) ?? 'Nieko nerasta',
  )

  const commit = async () => {
    if (grams == null || grams <= 0) return
    if (selected) await logFood(selected, { slot, quantityG: grams, logDate: date })
    else if (selectedRecipe)
      await logRecipe(selectedRecipe, { slot, quantityG: grams, logDate: date })
    else return
    if (cooked)
      await logCookingOil(COOKING_LABEL[method], cooked, { slot, quantityG: 0, logDate: date })
    void syncNow()
    onOpenChange(false)
    const kcal = Math.round((cooked ?? preview)?.kcal ?? 0)
    maybeLogToast(await logContext(date, slot, kcal, chosenName))
  }

  /**
   * Log a counter item exactly as it comes.
   *
   * Search offers the default build rather than opening the builder, because
   * the default is what almost everyone orders and a search result that leads
   * to another screen is not a search result. Swapping the milk still lives in
   * the chain's own tab, one tap away.
   */
  const logCounterItem = async (
    item: FastfoodItem,
    chainName: string,
    built: ReturnType<typeof asOrdered>,
    cup: { label: string; ml: number } | undefined,
  ) => {
    const suffix = cup ? ` ${cup.label}` : ''
    const itemSlot = slotFor(item.category, item.unit, item.name)
    const loggedName = `${chainName ? `${chainName} · ` : ''}${item.name}${suffix}`
    await logAdHoc(loggedName, built.totals, {
      slot: itemSlot,
      quantityG: Math.max(1, built.weightG),
      servings: 1,
      logDate: date,
    })
    void syncNow()
    onOpenChange(false)
    maybeLogToast(await logContext(date, itemSlot, Math.round(built.totals.kcal), loggedName))
  }

  const back = () => {
    setSelected(null)
    setSelectedRecipe(null)
    // The method belongs to the food that was on screen, not to the sheet.
    // Leaving it set meant picking an apple next quietly logged a spoonful of
    // frying oil alongside it, because the last food had been pan-fried.
    setMethod('raw')
  }

  // Labelled only when both lists are on screen: one list needs no heading to
  // say what it is, and the sheet's own title already said "produktą".
  const both = placed.items.length > 0 && results.length > 0

  const counterSection = placed.items.length > 0 && (
    <>
      <SectionLabel>Prie prekystalio</SectionLabel>
      <List>
        {placed.items.map((item, i) => {
          const chain = chains.find((c) => c.id === item.chainId)
          const cups = cupsForItem(item, chain?.drinkSizes ?? null)
          const built = asOrdered(item, cups)
          const cup = cups[0]
          return (
            <ListRow
              key={`ff-${item.id}`}
              last={i === placed.items.length - 1}
              title={item.name}
              subtitle={`${chain?.name ?? 'Tinklas'} · ${cup ? `${cup.label} ${cup.ml} ml` : '1 porcija'}`}
              value={`${Math.round(built.totals.kcal)}`}
              meta="kcal"
              onActivate={() => void logCounterItem(item, chain?.name ?? '', built, cup)}
            />
          )
        })}
      </List>
    </>
  )

  const shopSection = results.length > 0 && (
    <>
      {both && <SectionLabel>Parduotuvėje</SectionLabel>}
      <List>
        {results.map((food, i) => (
          <ListRow
            key={food.id}
            last={i === results.length - 1}
            title={foodDisplayName(food)}
            subtitle={`B ${Math.round(food.protein)} · A ${Math.round(food.carbs)} · R ${Math.round(food.fat)}`}
            value={`${Math.round(food.kcal)}`}
            meta={`kcal/100 ${food.unit}`}
            onActivate={() => {
              setSelected(food)
              setGrams(food.servingSizeG ?? 100)
              setMethod('raw')
            }}
          />
        ))}
      </List>
    </>
  )

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      // The list is full height so it stops resizing under the thumb as results
      // arrive; the form that follows is as tall as what it asks for.
      size={chosen ? 'auto' : 'full'}
      title={chosen ? chosenName : 'Pridėti produktą'}
      footer={
        chosen ? (
          <div className="flex gap-2">
            <Button block onClick={back}>
              Atgal
            </Button>
            <Button variant="primary" block onClick={() => void commit()} disabled={!grams}>
              Įrašyti {cooked ? `${Math.round(cooked.kcal)} kcal` : ''}
            </Button>
          </div>
        ) : undefined
      }
    >
      {chosen == null ? (
        <div className="flex flex-col">
          <div className="sticky top-0 z-10 flex items-center gap-2 bg-surface px-4 py-3">
            <SearchInput
              value={query}
              onValueChange={setQuery}
              label="Ieškoti produkto"
              placeholder="Ieškoti produkto…"
              tone="bg"
            />
            {onVoice && (
              <IconButton label="Įrašyti balsu" onClick={onVoice}>
                <MicIcon />
              </IconButton>
            )}
          </div>
          {query.trim().length < 2 && (
            <>
              <div className="px-4 pb-2">
                <Segmented
                  label="Greitas pasirinkimas"
                  value={tab}
                  onValueChange={(value) => setTab(value as QuickTab)}
                  className="w-full"
                  options={[
                    { value: 'recent', label: 'Neseniai' },
                    { value: 'often', label: 'Dažniausiai' },
                    { value: 'starred', label: 'Mėgstami' },
                  ]}
                />
              </div>
              {(quick ?? []).length === 0 ? (
                <EmptyState
                  title={quickEmptyTitle ?? ''}
                  hint={
                    tab === 'starred'
                      ? 'Pasirinkę produktą, pažymėkite jį mėgstamu — jis liks čia.'
                      : 'Įrašius kelis produktus, jie atsiras čia.'
                  }
                />
              ) : (
                <List>
                  {(quick ?? []).map((item, i) => (
                    <ListRow
                      key={item.id}
                      last={i === (quick ?? []).length - 1}
                      title={item.name}
                      subtitle={
                        item.kind === 'recipe'
                          ? 'Receptas'
                          : `${item.count}× · ${Math.round(item.per100g.protein)} B`
                      }
                      value={`${Math.round(item.per100g.kcal)}`}
                      meta={`kcal/100 ${item.unit}`}
                      onActivate={() => void pickQuick(item)}
                    />
                  ))}
                </List>
              )}
            </>
          )}

          {searching && results.length === 0 && (
            <div className="flex justify-center py-6">
              <PersonaSpinner surface="search" />
            </div>
          )}
          {searchEmpty && (
            <EmptyState
              title={searchEmptyTitle ?? ''}
              hint="Nuskaitykite produkto etiketę — arba, jei valgote ne namie, įvertinkite patiekalą."
              action={
                // The moment the search gives up is exactly the moment someone
                // is sitting in a restaurant, so the way out is offered here
                // rather than hidden behind another screen.
                <Button onClick={() => setEstimating(true)}>Įvertinti patiekalą</Button>
              }
            />
          )}

          {placed.first ? (
            <>
              {counterSection}
              {shopSection}
            </>
          ) : (
            <>
              {shopSection}
              {counterSection}
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4 px-4 py-4">
          <SlotPicker value={slot} onChange={setSlot} />
          <NumberField label="Kiekis" suffix={unit} value={grams} onChange={setGrams} />
          <QuantityPresets
            unit={unit}
            value={grams}
            servingSizeG={selected?.servingSizeG ?? null}
            onPick={setGrams}
          />

          {unit === 'g' && (
            <CookingPicker
              key={chosen.id}
              value={method}
              onChange={setMethod}
              cooked={cooked}
              defaultOpen={cookable}
            />
          )}

          <div className="flex h-11 items-center justify-between">
            <Switch
              label="Mėgstamas"
              checked={starred}
              onCheckedChange={() => {
                if (!chosen) return
                void toggleFavourite(selected ? 'food' : 'recipe', chosen.id).then(setStarred)
              }}
            />
          </div>
          {selected?.servingName != null && (
            <p className="text-sm text-mute">Porcija: {selected.servingName}</p>
          )}
          {preview && (
            <MacroPreview
              kcal={preview.kcal}
              proteinG={preview.protein}
              carbsG={preview.carbs}
              fatG={preview.fat}
            />
          )}
        </div>
      )}

      <DishEstimateSheet
        open={estimating}
        date={date}
        initialDescription={query}
        onOpenChange={setEstimating}
        onLogged={() => onOpenChange(false)}
      />
    </Sheet>
  )
}
